import React, { useCallback } from "react"

import { Button } from "@coval/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { GripVertical, Trash2 } from "lucide-react"

import type { AudioFilterEntryConfig } from "@/types/metrics/metadata"

import {
  AUDIO_FILTER_TYPES,
  createDefaultAudioFilterEntry,
  type AudioFilterEntryType,
} from "../types"
import { MusicFilterEditor } from "./MusicFilterEditor"
import { OverlapFilterEditor } from "./OverlapFilterEditor"
import { SilenceFilterEditor } from "./SilenceFilterEditor"
import { SpeakerFilterEditor } from "./SpeakerFilterEditor"
import { TimeRangeFilterEditor } from "./TimeRangeFilterEditor"
import { TurnRangeFilterEditor } from "./TurnRangeFilterEditor"

interface FilterRowProps {
  filter: AudioFilterEntryConfig
  onChange: (filter: AudioFilterEntryConfig) => void
  onRemove: () => void
  supportedFilterTypes?: readonly AudioFilterEntryType[]
  // Forwarded to SpeakerFilterEditor (radios vs checkboxes); ignored elsewhere.
  singleSpeakerSelect?: boolean
  // Forwarded to SpeakerFilterEditor; restricts the picker to agent only.
  agentOnly?: boolean
}

export const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  onChange,
  onRemove,
  supportedFilterTypes,
  singleSpeakerSelect,
  agentOnly,
}) => {
  const visibleFilterTypes = supportedFilterTypes
    ? AUDIO_FILTER_TYPES.filter((t) => supportedFilterTypes.includes(t.value))
    : AUDIO_FILTER_TYPES
  const handleTypeChange = useCallback(
    (value: string) => {
      const newType = value as AudioFilterEntryType
      if (newType === filter.type) {
        return
      }
      onChange(createDefaultAudioFilterEntry(newType))
    },
    [filter.type, onChange],
  )

  return (
    <div className="flex items-center gap-2">
      <GripVertical
        className="w-4 h-4 text-coval-text-muted cursor-grab active:cursor-grabbing shrink-0"
        aria-hidden
      />
      <div className="flex-1 flex items-center gap-3">
        <Select value={filter.type} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-32" aria-label="Audio filter type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {visibleFilterTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1">
          {filter.type === "speaker" && (
            <SpeakerFilterEditor
              filter={filter}
              onChange={onChange}
              singleSelect={singleSpeakerSelect}
              agentOnly={agentOnly}
            />
          )}
          {filter.type === "silence" && (
            <SilenceFilterEditor filter={filter} onChange={onChange} />
          )}
          {filter.type === "overlap" && <OverlapFilterEditor />}
          {filter.type === "music" && <MusicFilterEditor />}
          {filter.type === "time_range" && (
            <TimeRangeFilterEditor filter={filter} onChange={onChange} />
          )}
          {filter.type === "turn_range" && (
            <TurnRangeFilterEditor filter={filter} onChange={onChange} />
          )}
        </div>
      </div>

      <Button
        variant="ghost"
        icon
        onClick={onRemove}
        tooltip="Remove audio filter"
        className="text-coval-text-muted hover:text-coval-danger-text"
      >
        <Trash2 />
      </Button>
    </div>
  )
}
